import { useRouter } from 'expo-router';
import { DateTime } from 'luxon';
import * as React from 'react';
import { View } from 'react-native';
import Modal from 'react-native-modal';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { Reservation } from '@/api';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import {
  formatIsoInZone,
  formatNumber,
  getCancellationScenario,
  getCancelState,
  getExtrasCost,
} from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { Button, Text } from '@/ui';
import { Chip, ChipType } from '@/ui/chip';
import { IconBadge } from '@/ui/icon-badge';
import { Car } from '@/ui/icons/car';

function TripInfoCard({
  resData,
  chipText,
  chipType,
}: {
  resData: Reservation;
  chipText: string;
  chipType: ChipType;
}) {
  const { styles } = useStyles(stylesheet);
  const tz = resData.pickupLocation.timezone;

  return (
    <View style={styles.tripInfoCard}>
      <View style={styles.tripInfoCardMain}>
        <IconBadge icon={<Car />} iconSize={24} />

        <View style={styles.gap4}>
          <Text type="bodyRegular">
            {resData.car.brandName} {resData.car.modelName}
          </Text>

          <Text type="caption1" style={styles.textSecondary}>
            {formatIsoInZone(resData.startTime, tz, 'MMM d')} to{' '}
            {formatIsoInZone(resData.endTime, tz, 'MMM d')}
          </Text>
        </View>
      </View>

      <Chip type={chipType} text={chipText} />
    </View>
  );
}

export function useCancelTripModalBody(
  resData: Reservation | undefined,
  now: Date
): React.ReactNode | null {
  const { styles } = useStyles(stylesheet);

  return React.useMemo(() => {
    if (!resData) return null;

    const cancelState = getCancelState(resData, now);

    const formatCancelUntil = (fmt: string) =>
      DateTime.fromISO(resData.cancellationPolicy.cancelUntil, { zone: 'utc' })
        .setZone(resData.pickupLocation.timezone)
        .toFormat(fmt);

    const wrap = (children: React.ReactNode) => (
      <View style={styles.gap16}>{children}</View>
    );

    switch (cancelState) {
      case 'partial-refund': {
        const [firstRate] = resData.reservationRates;
        const dayOneDiscount = firstRate.discountAmount
          ? firstRate.discountAmount / firstRate.numDays
          : 0;
        const oneDayCharge =
          firstRate.dailyRate -
          dayOneDiscount +
          (resData.insuranceCost > 0
            ? resData.insuranceCost / resData.numDays
            : 0) +
          resData.totalDeliveryCost +
          resData.totalCancellationPolicyCost +
          getExtrasCost(resData);
        const partialRefundAmount = resData.totalCost - oneDayCharge;

        return wrap(
          <>
            <TripInfoCard
              resData={resData}
              chipText="Partial refund"
              chipType={ChipType.WARNING}
            />

            <Text type="bodyRegular">
              Full refund until {formatCancelUntil('h:mm a ZZZZ, MMM dd')}.
              Cancelling now costs one day of rental, $
              {formatNumber(oneDayCharge)}. The remaining $
              {formatNumber(partialRefundAmount)} goes back as Eon credit.
            </Text>
          </>
        );
      }
      case 'fully-refundable':
      case 'non-refundable-grace':
        return wrap(
          <>
            <TripInfoCard
              resData={resData}
              chipText="Full refund"
              chipType={ChipType.SUCCESS}
            />

            <Text type="bodyRegular">
              Full refund until {formatCancelUntil('h:mm a ZZZZ, MMM dd')}. Then
              $
              {formatNumber(
                resData.totalCost - resData.totalCancellationPolicyCost
              )}{' '}
              will be refunded to your original payment method.
            </Text>
          </>
        );
      case 'non-refundable':
      default:
        return wrap(
          <>
            <TripInfoCard
              resData={resData}
              chipText="Non-refundable"
              chipType={ChipType.ERROR}
            />

            <Text type="bodyRegular">
              {resData.cancellationPolicy
                ? `Free cancellation ended ${formatCancelUntil(
                    'h:mm a ZZZZ, MMM dd'
                  )}. Cancelling now means you won't get your $${formatNumber(
                    resData.totalCost
                  )} back`
                : 'This booking is non-refundable. If you cancel, no refund will be issued for this reservation.'}
            </Text>
          </>
        );
    }
  }, [resData, now, styles]);
}

type CancelTripModalProps = {
  visible: boolean;
  onClose: () => void;
  reservation: Reservation | undefined;
};

export function CancelTripModal({
  visible,
  onClose,
  reservation,
}: CancelTripModalProps) {
  const { styles } = useStyles(stylesheet);
  const router = useRouter();
  const body = useCancelTripModalBody(reservation, new Date());
  const cancelState = reservation ? getCancelState(reservation) : undefined;
  const hasTrackedStarted = React.useRef(false);

  React.useEffect(() => {
    if (visible) hasTrackedStarted.current = false;
  }, [visible]);

  const trackCancellationStarted = (outcome: 'Cancel Trip' | 'Keep Trip') => {
    if (hasTrackedStarted.current || !reservation) return;
    hasTrackedStarted.current = true;

    mixpanelService.track('Cancellation Started', {
      Outcome: outcome,
      'Cancellation Scenario': getCancellationScenario(
        getCancelState(reservation)
      ),
    });
  };

  const handleKeepTrip = () => {
    trackCancellationStarted('Keep Trip');
    onClose();
  };

  if (!reservation || !body) return null;

  const handleCancelTrip = () => {
    trackCancellationStarted('Cancel Trip');
    onClose();
    router.push(`/trips/${reservation.id}/cancel`);
  };

  const isNonRefundable = cancelState === 'non-refundable';

  const keepTripButton = (
    <Button
      type="ghost"
      tx="tripDetails.cancelTripModalLeftButtonKeepTrip"
      onPress={handleKeepTrip}
      style={isNonRefundable ? styles.buttonAuto : styles.buttonResponsiveAuto}
    />
  );

  const cancelTripButton = (
    <Button
      type="primary"
      tx="tripDetails.cancelTripModalRightButton"
      onPress={handleCancelTrip}
      style={
        isNonRefundable
          ? styles.buttonAutoMinWidth
          : styles.buttonResponsiveAutoMinWidth
      }
    />
  );

  return (
    <Modal
      animationIn="slideInUp"
      animationInTiming={300}
      animationOut="slideOutDown"
      animationOutTiming={1000}
      isVisible={visible}
      hasBackdrop
      onBackdropPress={handleKeepTrip}
      onDismiss={handleKeepTrip}
      style={styles.modalContainer}
    >
      <View style={styles.dialog}>
        <View style={styles.header}>
          <View style={styles.headerText}>
            <Text tx="tripDetails.cancelTripModalTitle" type="title2" />

            <Text
              tx="tripDetails.cancelTripModalSubtitle"
              type="bodyRegular"
              style={styles.textSecondary}
            />
          </View>

          <NavigationButton
            type={NavigationButtonTypes.cancel}
            onPress={handleKeepTrip}
          />
        </View>

        {body}

        <View style={styles.divider} />

        <View
          style={[
            styles.buttonRow,
            isNonRefundable ? styles.buttonRowAlwaysRow : null,
          ]}
        >
          {keepTripButton}
          {cancelTripButton}
        </View>
      </View>
    </Modal>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  modalContainer: {
    margin: 0,
    paddingHorizontal: {
      xs: 16,
      md: 0,
    },
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    maxWidth: {
      xs: '100%',
      sm: 600,
      md: 800,
    },
    width: {
      xs: '100%',
      md: 600,
    },
  },
  dialog: {
    width: '100%',
    gap: 24,
    backgroundColor: theme.colors.dialog.backgroundColor,
    padding: 16,
    borderRadius: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 8,
  },
  headerText: {
    flex: 1,
    gap: 8,
  },
  gap4: {
    gap: 4,
  },
  gap8: {
    gap: 8,
  },
  gap16: {
    gap: 16,
  },
  textSecondary: {
    color: theme.colors.text.secondary,
  },
  tripInfoCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: 16,
    borderRadius: 16,
    backgroundColor: theme.colors.card.hover.backgroundColor,
  },
  tripInfoCardMain: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 13,
    flex: 1,
  },
  divider: {
    height: 1,
    width: '100%',
    backgroundColor: theme.colors.card.default.borderColor,
  },
  buttonRow: {
    flexDirection: {
      xs: 'column-reverse',
      md: 'row',
    },
    justifyContent: 'flex-end',
    gap: 8,
  },
  buttonRowAlwaysRow: {
    flexDirection: 'row',
  },
  buttonAuto: {
    width: 'auto',
  },
  buttonAutoMinWidth: {
    width: 'auto',
    minWidth: 150,
  },
  buttonResponsiveAuto: {
    width: {
      xs: '100%',
      md: 'auto',
    },
  },
  buttonResponsiveAutoMinWidth: {
    width: {
      xs: '100%',
      md: 'auto',
    },
    minWidth: {
      md: 150,
    },
  },
}));
